import { useDispatch } from "react-redux";
import axios from "./axios";
import { postDataAPI } from "./fetchData";

// Helper function to check file type
const isValidImageType = (file) => {
  const validTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/heic',
    'image/heif',
    'image/webp'
  ];
  return validTypes.includes(file.type.toLowerCase());
};

// Helper function to check PDF type
const isValidPDFType = (file) => {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
};

// Helper function to check file size
const isValidFileSize = (file, maxSizeMB = 10) => {
  return file.size <= maxSizeMB * 1024 * 1024;
};

// Helper function to validate supported file types for AI processing
export const isValidAIFileType = (file) => {
  return isValidImageType(file) || 
         isValidPDFType(file) || 
         file.type === 'text/csv' || 
         file.type.startsWith('text/') || 
         file.name.toLowerCase().match(/\.(csv|txt|json|xml|md)$/);
};

// Note: PDF files are processed directly through AI, no separate upload needed

export const uploadImage = async (file) => {
  if (!file) {
    console.error('No file provided to uploadImage');
    return false;
  }

  // Get token from localStorage
  const token = localStorage.getItem('dizeeToken');

  try {
    console.log('Starting image upload process for:', file.name);
    
    // Validate file type and size
    if (!isValidImageType(file)) {
      console.error('Invalid file type:', file.type);
      throw new Error('Invalid file type. Please upload a valid image file.');
    }

    if (!isValidFileSize(file)) {
      console.error('File too large:', file.size);
      throw new Error('File size too large. Maximum size is 10MB.');
    }

    // Process image before upload
    const processedFile = await processImageForUpload(file);
    console.log('Image processed successfully');
    
    // Create FormData with processed image
    const formData = new FormData();
    
    // Handle iOS HEIC/HEIF format
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isHeicFormat = ['heic', 'heif'].includes(fileExtension);
    
    // Rename file with timestamp and proper extension
    const fileName = `image-${Date.now()}.${isHeicFormat ? 'jpg' : fileExtension}`;
    const finalFile = new File(
      [processedFile], 
      fileName,
      { 
        type: isHeicFormat ? 'image/jpeg' : processedFile.type,
        lastModified: Date.now()
      }
    );

    formData.append('image', finalFile);

    // Implement retry logic
    const maxRetries = 3;
    let attempt = 0;
    let lastError = null;

    while (attempt < maxRetries) {
      try {
        console.log(`Upload attempt ${attempt + 1} of ${maxRetries}`);
        
        const imageResponse = await postDataAPI('aws/imageUpload', formData, token, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('Upload progress:', percentCompleted + '%');
          }
        });

        console.log('Server response:', imageResponse);
        const imageUrl = imageResponse?.data?.data?.imageUrl;
        
        if (imageUrl) {
          console.log('Image uploaded successfully:', imageUrl);
          return imageUrl;
        }

        console.error('No imageUrl in response:', imageResponse);
        throw new Error('No image URL received from server');
      } catch (err) {
        console.error(`Attempt ${attempt + 1} failed:`, {
          message: err.response?.data?.message || err.message,
          status: err.response?.status,
          statusText: err.response?.statusText
        });
        
        lastError = err;
        if (attempt === maxRetries - 1) break;
        
        const backoffTime = Math.min(1000 * Math.pow(2, attempt), 5000);
        console.log(`Retrying in ${backoffTime}ms...`);
        await new Promise(resolve => setTimeout(resolve, backoffTime));
        attempt++;
      }
    }

    console.error('Upload failed after all retries:', lastError);
    throw lastError;

  } catch (err) {
    console.error('Final error details:', {
      message: err.response?.data?.message || err.message,
      status: err.response?.status,
      statusText: err.response?.statusText,
      stack: err.stack
    });
    return false;
  }
};

// Reference existing processImageForUpload function
const processImageForUpload = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        let width = img.width;
        let height = img.height;
        const maxDimension = 1200;

        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            const processedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(processedFile);
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};